import { useQueryClient } from '@tanstack/react-query'
import React, { useState } from 'react'
import { toast } from 'sonner'

import { Button, Textarea } from 'slab'
import {
  getGetBrokersTasksTaskTypesQueryKey,
  getGetBrokersTasksV2HoldsQueryKey,
  getGetBrokersTasksV2StatisticsQueryKey,
  usePatchBrokersTasksTaskTypes,
} from 'utils/web_api'

interface BulkUpdateTaskTypesDialogProps {
  isOpen: boolean
  onClose: () => void
  selectedTaskTypes: string[]
  onSuccess?: () => void
}

const BulkUpdateTaskTypesDialog: React.FC<BulkUpdateTaskTypesDialogProps> = ({
  isOpen,
  onClose,
  selectedTaskTypes,
  onSuccess,
}) => {
  const queryClient = useQueryClient()
  const [action, setAction] = useState<'add_holds' | 'remove_holds'>('add_holds')
  const [message, setMessage] = useState('')

  const bulkUpdateMutation = usePatchBrokersTasksTaskTypes()

  const handleSubmit = async () => {
    if (action === 'add_holds' && !message.trim()) {
      toast.error('Please provide a message for the hold')
      return
    }

    try {
      await bulkUpdateMutation.mutateAsync({
        data: {
          task_types: selectedTaskTypes,
          action,
          message: message.trim() || undefined,
        },
      })

      toast.success(
        `Successfully ${action === 'add_holds' ? 'added' : 'removed'} holds for ${selectedTaskTypes.length} task type(s)`
      )

      // Invalidate relevant queries to refresh data using generated query key functions
      await queryClient.invalidateQueries({
        queryKey: getGetBrokersTasksTaskTypesQueryKey(),
      })
      await queryClient.invalidateQueries({
        queryKey: getGetBrokersTasksV2StatisticsQueryKey(),
      })
      await queryClient.invalidateQueries({
        queryKey: getGetBrokersTasksV2HoldsQueryKey(),
      })

      setMessage('')
      setAction('add_holds')
      onSuccess?.()
      onClose()
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to update holds'
      toast.error(errorMessage)
    }
  }

  if (!isOpen) return null

  return (
    <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'>
      <div className='bg-white p-6 rounded-lg shadow-lg max-w-md w-full mx-4 max-h-[80vh] overflow-y-auto'>
        <h3 className='text-lg font-semibold mb-4'>
          Bulk Update Holds
        </h3>

        <div className='space-y-4'>
          {/* Selected Task Types Count */}
          <div className='bg-gray-50 p-3 rounded border border-gray-200'>
            <p className='text-sm text-gray-700'>
              <span className='font-medium'>{selectedTaskTypes.length}</span> task type(s) selected
            </p>
          </div>

          {/* Action Selection */}
          <div>
            <label className='block text-sm font-medium text-gray-700 mb-2'>
              Action
            </label>
            <div className='flex gap-2'>
              <Button
                variant={action === 'add_holds' ? 'tertiary' : 'tertiary-deemphasized'}
                size='small'
                onClick={() => setAction('add_holds')}
              >
                Add Holds
              </Button>
              <Button
                variant={action === 'remove_holds' ? 'tertiary' : 'tertiary-deemphasized'}
                size='small'
                onClick={() => setAction('remove_holds')}
              >
                Remove Holds
              </Button>
            </div>
          </div>

          {/* Message Input (only for add_holds) */}
          {action === 'add_holds' && (
            <div>
              <label htmlFor='message' className='block text-sm font-medium text-gray-700 mb-2'>
                Message / Reason *
              </label>
              <Textarea
                id='message'
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder='Enter reason for hold...'
                rows={3}
              />
              <p className='text-xs text-gray-500 mt-1'>
                This message will be prefixed with "[Bulk Hold]"
              </p>
            </div>
          )}

          {/* Confirmation message for remove_holds */}
          {action === 'remove_holds' && (
            <div className='bg-amber-50 border border-amber-200 p-3 rounded'>
              <p className='text-sm text-amber-800'>
                This will clear all active holds for the selected task types.
              </p>
            </div>
          )}

          {/* Action Buttons */}
          <div className='flex gap-2 pt-4'>
            <Button
              variant='secondary'
              onClick={onClose}
              disabled={bulkUpdateMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant='primary'
              onClick={handleSubmit}
              disabled={bulkUpdateMutation.isPending || (action === 'add_holds' && !message.trim())}
            >
              {bulkUpdateMutation.isPending
                ? 'Updating...'
                : action === 'add_holds'
                  ? 'Add Holds'
                  : 'Remove Holds'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default BulkUpdateTaskTypesDialog
